/**
 * Offline-tolerant time entry (features §12): when a POST /time-entries fails
 * because the network is down, the entry is queued in localStorage and sent
 * when the browser is back online (or on next load). Only creates are queued —
 * edits and deletes need the server's current state to be safe.
 */
import { api, ApiError, type TimeEntry } from './api.js'

const KEY = 'apm_offline_time_entries'
export interface Queued { id: string; body: Record<string, unknown>; queuedAt: string }
type Listener = (n: number) => void
const listeners = new Set<Listener>()

export const readQueue = (): Queued[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as Queued[] } catch { return [] } }
const writeQueue = (q: Queued[]) => { localStorage.setItem(KEY, JSON.stringify(q)); for (const l of listeners) l(q.length) }
export const onQueueChange = (l: Listener) => { listeners.add(l); l(readQueue().length); return () => { listeners.delete(l) } }

export const isNetworkError = (e: unknown) => e instanceof TypeError || (e instanceof Error && /Failed to fetch|NetworkError|Load failed/i.test(e.message))

/** Try to create; if offline, queue and resolve with `{ queued: true }`. */
export async function createEntryOfflineTolerant(body: Record<string, unknown>): Promise<{ queued: boolean; entry?: TimeEntry; overlaps?: unknown[] }> {
  try {
    const r = await api<{ entry: TimeEntry; overlaps: unknown[] }>('/time-entries', { method: 'POST', json: body })
    return { queued: false, ...r }
  } catch (e) {
    if (isNetworkError(e) || !navigator.onLine) {
      writeQueue([...readQueue(), { id: crypto.randomUUID(), body, queuedAt: new Date().toISOString() }])
      return { queued: true }
    }
    throw e
  }
}

/** Send everything queued; stop at the first network failure, drop items the server rejects (4xx) after reporting them. */
export async function flushQueue(report: (msg: string) => void): Promise<number> {
  let sent = 0
  for (const item of readQueue()) {
    try {
      await api('/time-entries', { method: 'POST', json: item.body })
      writeQueue(readQueue().filter((q) => q.id !== item.id)); sent++
    } catch (e) {
      if (e instanceof ApiError) { writeQueue(readQueue().filter((q) => q.id !== item.id)); report(`A queued entry from ${new Date(item.queuedAt).toLocaleString()} was rejected: ${e.message}`) ; continue }
      break   // still offline
    }
  }
  return sent
}

export function startOfflineSync(report: (msg: string) => void) {
  const tryFlush = () => { if (navigator.onLine && readQueue().length) void flushQueue(report) }
  window.addEventListener('online', tryFlush)
  const t = setInterval(tryFlush, 60_000)
  tryFlush()
  return () => { window.removeEventListener('online', tryFlush); clearInterval(t) }
}
